import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  Checkbox,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add, Check, PlaylistAddCheck } from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { ActionsButton, SettingsCard } from '../../components/composed';
import { FacilityForm } from '../../components/domain/facility';
import { ApproveOrDeny } from '../../components/domain/object_management';
import CallToAction from '../DataBuilderPage/CallToAction';
import { getCsrfToken } from '../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.display({
    id: 'select',
    enableSorting: false,
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': 'Select all facilities' }}
        size="small"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': `Select facility ${row.original.id}` }}
        size="small"
      />
    ),
  }),
  columnHelper.accessor((row) => row.name ?? '', {
    id: 'name',
    header: 'Facility Name',
    sortingFn: 'alphanumeric',
    cell: (info) => info.getValue() || 'N/A',
  }),
  columnHelper.accessor((row) => row.address?.city ?? '', {
    id: 'city',
    header: 'City',
    sortingFn: 'alphanumeric',
    cell: (info) => info.getValue() || 'N/A',
  }),
  columnHelper.accessor((row) => row.address?.state ?? '', {
    id: 'state',
    header: 'State',
    sortingFn: 'alphanumeric',
    cell: (info) => info.getValue() || 'N/A',
  }),
  columnHelper.accessor('is_approved', {
    id: 'is_approved',
    header: 'Verified',
    sortingFn: 'basic',
    cell: (info) => (info.getValue() ? <Check sx={{ color: '#4caf50' }} /> : null),
  }),
];

const Facilities = () => {
  const theme = useTheme();
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [open, setOpen] = useState(false);
  const [sorting, setSorting] = useState([{ id: 'city', desc: false }]);
  const [rowSelection, setRowSelection] = useState({});
  const [manageOpen, setManageOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const navigate = useNavigate();

  const table = useReactTable({
    data: facilities,
    columns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => String(row.id),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const selectedFacilityIds = table
    .getSelectedRowModel()
    .rows.map((row) => row.original.id);

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/facilities/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFacilities(data);
    } catch (err) {
      console.error('Error fetching facilities:', err);
      setError('Failed to load facilities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (facilityId) => {
    setSelectedFacilityId(facilityId);
    navigate(`/facilities/${facilityId}`);
  };

  const handleAddFacility = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSaveSuccess = (savedFacility) => {
    // Add new facility to the list
    setFacilities([savedFacility, ...facilities]);
    // Close modal
    setOpen(false);
  };

  const handleManageSelected = () => {
    setBulkError(null);
    setManageOpen(true);
  };

  const handleManageClose = () => {
    if (!bulkSubmitting) {
      setManageOpen(false);
      setBulkError(null);
    }
  };

  const handleBulkModeration = async (updates) => {
    try {
      setBulkSubmitting(true);
      setBulkError(null);

      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/facilities/bulk/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ ids: selectedFacilityIds, ...updates }),
      });

      if (!response.ok) {
        let message = 'Failed to update the selected facilities. Please try again.';
        if (response.status === 403) {
          message = 'You do not have permission to manage facilities.';
        } else {
          try {
            const data = await response.json();
            message = data.error || data.detail || message;
          } catch (parseError) {
            // Fall back to the generic message when the body is not JSON
          }
        }
        setBulkError(message);
        return;
      }

      setManageOpen(false);
      setRowSelection({});
      fetchFacilities();
    } catch (err) {
      console.error('Error updating selected facilities:', err);
      setBulkError('Network error. Please check your connection and try again.');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const actionsButton = (
    <ActionsButton
      actions={[
        {
          id: 'add-facility',
          label: 'Add Facility',
          icon: <Add fontSize="small" />,
          onClick: handleAddFacility,
        },
        {
          id: 'manage-selected-facilities',
          label: 'Manage Selected Facilities',
          icon: <PlaylistAddCheck fontSize="small" />,
          disabled: selectedFacilityIds.length === 0,
          onClick: handleManageSelected,
        },
      ]}
    />
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard
            title="Facilities"
            sx={{ mb: 0 }}
            endActions={actionsButton}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard
            title="Facilities"
            sx={{ mb: 0 }}
            endActions={actionsButton}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        padding: 1,
      }}
    >
      <Box>
        <SettingsCard
          title="Facilities"
          sx={{ mb: 0 }}
          endActions={actionsButton}
        >
          {facilities.length === 0 ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No facilities found.
                </Typography>
              </Box>
              <CallToAction />
            </>
          ) : (
            <>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          const canSort = header.column.getCanSort();
                          const sortDirection = header.column.getIsSorted();
                          return (
                            <th
                              key={header.id}
                              onClick={
                                canSort
                                  ? header.column.getToggleSortingHandler()
                                  : undefined
                              }
                              style={{
                                textAlign: 'left',
                                paddingRight: 10,
                                borderBottom: `1px solid ${theme.palette.primary.main}`,
                                ...(canSort ? { cursor: 'pointer' } : {}),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {sortDirection === 'asc'
                                ? ' \u25B2'
                                : sortDirection === 'desc'
                                ? ' \u25BC'
                                : ''}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => handleRowClick(row.original.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            style={{
                              textAlign: 'left',
                              paddingRight: 10,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
              <CallToAction />
            </>
          )}
        </SettingsCard>
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <FacilityForm
            onSaved={handleSaveSuccess}
            showActions={true}
            submitLabel="Submit"
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={manageOpen}
        onClose={handleManageClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <ApproveOrDeny
            title="Manage selected facilities"
            message={`${selectedFacilityIds.length} ${
              selectedFacilityIds.length === 1 ? 'facility' : 'facilities'
            } selected. Approving marks them as verified. Denying removes them from the list.`}
            errorMessage={bulkError}
            disabled={bulkSubmitting}
            onApprove={() => handleBulkModeration({ is_approved: true })}
            onDeny={() => handleBulkModeration({ is_active: false })}
            onCancel={handleManageClose}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Facilities;
